import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface AuthContextType {
  isLoggedIn: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Always start with logged out state for demo purposes
  useEffect(() => {
    // Clear any existing auth state
    localStorage.removeItem("isLoggedIn");
    setIsLoggedIn(false);
    console.log("Auth state reset - user must login");
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // For demo purposes, accept any valid email/password
      if (email && password.length >= 6) {
        setIsLoggedIn(true);
        localStorage.setItem("isLoggedIn", "true");
        console.log("Login successful, redirecting to home");
      } else {
        throw new Error("Thông tin đăng nhập không hợp lệ");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Đăng nhập thất bại. Vui lòng thử lại.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    console.log("Logging out user");
    setIsLoggedIn(false);
    setError(null);
    localStorage.removeItem("isLoggedIn");
    console.log("User logged out successfully");
  };

  const value: AuthContextType = {
    isLoggedIn,
    isLoading,
    error,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
